import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Pencil, Trophy } from "lucide-react";
import { Input, Select } from "./Warehouse";

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [show, setShow] = useState(false);
  const emptyForm = { username: "", full_name: "", password: "", role: "gudang", branch_id: "" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [u, b] = await Promise.all([api.get("/users"), api.get("/branches")]);
    setRows(u.data); setBranches(b.data);
  };
  useEffect(() => { load(); }, []);

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({ username: u.username, full_name: u.full_name, password: "", role: u.role, branch_id: u.branch_id || "" });
    setShow(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = {
          full_name: form.full_name,
          role: form.role,
          branch_id: form.role === "cabang" ? form.branch_id : null,
        };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editingId}`, payload);
        toast.success("Data karyawan diperbarui");
      } else {
        await api.post("/users", { ...form, branch_id: form.role === "cabang" ? form.branch_id : null });
        toast.success("User dibuat");
      }
      setForm(emptyForm); setEditingId(null);
      setShow(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch { toast.error("Gagal"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Manajemen</p>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Karyawan & Akun</h1>
        </div>
        <button data-testid="new-user-btn" onClick={() => { setEditingId(null); setForm(emptyForm); setShow(!show); }} className="h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center gap-2 active:scale-[0.98] transition-transform">
          <UserPlus size={16} /> Baru
        </button>
      </div>

      {show && (
        <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h3 className="font-bold text-lg" style={{fontFamily:'Manrope'}}>{editingId ? "Edit Karyawan" : "Karyawan Baru"}</h3>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Username</span>
            <input data-testid="u-username" type="text" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required disabled={!!editingId} className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-base bg-white disabled:bg-slate-100 disabled:text-slate-500" />
            {editingId && <span className="text-xs text-slate-400">Username tidak bisa diubah (dipakai untuk login)</span>}
          </label>
          <Input testid="u-fullname" label="Nama Lengkap" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required />
          <Input testid="u-password" label={editingId ? "Password Baru (kosongkan jika tidak diganti)" : "Password"} type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required={!editingId} />
          <p className="text-[11px] text-slate-400 -mt-2">Minimal 6 karakter. Bagikan password ke staff secara langsung — tidak terkirim otomatis.</p>
          <Select testid="u-role" label="Peran" value={form.role} onChange={(v) => setForm({ ...form, role: v })} required options={[
            { value: "admin", label: "Admin / Bos" },
            { value: "gudang", label: "Staff Gudang Pusat" },
            { value: "cabang", label: "Staff Cabang Kota" },
            { value: "keuangan", label: "Admin Keuangan & Pencatatan" },
          ]} />
          {form.role === "cabang" && (
            <Select testid="u-branch" label="Cabang" value={form.branch_id} onChange={(v) => setForm({ ...form, branch_id: v })} required options={branches.map(b => ({ value: b.id, label: `${b.name} - ${b.city}` }))} />
          )}
          <div className="flex gap-2">
            <button data-testid="u-submit" type="submit" className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl">{editingId ? "Simpan Perubahan" : "Simpan"}</button>
            {editingId && (
              <button data-testid="u-cancel-edit" type="button" onClick={() => { setEditingId(null); setForm(emptyForm); setShow(false); }} className="h-12 px-4 rounded-xl border border-slate-300 text-slate-600 font-bold">Batal</button>
            )}
          </div>
        </form>
      )}

      <div className="space-y-2">
        {rows.map((u) => (
          <div key={u.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-slate-900" style={{fontFamily:'Manrope'}}>{u.full_name}</p>
                <span data-testid={`score-${u.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${(u.score ?? 100) >= 100 ? 'bg-emerald-100 text-emerald-800' : (u.score ?? 100) >= 80 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                  <Trophy size={10} /> Score {u.score ?? 100}
                </span>
              </div>
              <p className="text-xs text-slate-500">@{u.username} · {u.role}{u.branch_id ? ` · ${branches.find(b => b.id === u.branch_id)?.name || ''}` : ''}</p>
            </div>
            <div className="flex gap-1 flex-shrink-0 items-center">
              <button data-testid={`edit-u-${u.id}`} onClick={() => startEdit(u)} className="w-10 h-10 rounded-lg text-blue-600 hover:bg-blue-50 flex items-center justify-center" title="Edit karyawan"><Pencil size={16} /></button>
              <button data-testid={`toggle-${u.id}`} onClick={() => toggleActive(u)} className={`h-9 px-3 rounded-lg text-xs font-bold uppercase tracking-wider ${u.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                {u.active ? "Aktif" : "Nonaktif"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
